import React, { useCallback, useMemo, useState } from 'react';

import { Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { TABLET_SCREEN_SIZE } from '../../../../constants/constants';
import { CHEVRON_DOWN_V3, CHEVRON_UP_V3 } from '../../../../constants/images';
import useWindowSize from '../../../../hooks/useWindowSize/useWindowSize';
import { ITableColumn } from '../../../../interfaces/tables';
import { GenericObject, GenericVoidFunction } from '../../../../types/genericTypes';
import BdsIconButton from '../../Buttons/BdsIconButton/BdsIconButton';

import './StyledTableRow.scss';

interface ITableRow {
  row: GenericObject;
  columns: ITableColumn[];
  activeRow?: string;
  checked?: boolean;
  dataKey: string;
  onCheck?: GenericVoidFunction;
  onClick?: GenericVoidFunction;
  onChange?: GenericVoidFunction;
}

const TableRow = ({ row, columns, activeRow, checked, dataKey, onCheck, onClick, onChange }: ITableRow) => {
  const [showMore, setShowMore] = useState(false);

  const { width } = useWindowSize();

  const isDesktop = useMemo(() => {
    return width >= TABLET_SCREEN_SIZE;
  }, [width]);

  const isRowClickable = useMemo(() => {
    return (isDesktop && !!onClick) || activeRow !== undefined;
  }, [isDesktop, onClick, activeRow]);

  const { t } = useTranslation();

  const isActive = useMemo(() => {
    return activeRow === undefined || activeRow === row[dataKey];
  }, [activeRow, row, dataKey]);

  const rowClass = activeRow === undefined || activeRow === row[dataKey] ? 'clickable-row' : 'disabled-row';

  const onRowClickMobile = useCallback(() => {
    if (!isDesktop && onClick && activeRow === undefined) {
      onClick(row);
    }
  }, [isDesktop, onClick, row, activeRow]);

  const renderData = useCallback(
    (columnDetail: ITableColumn): string | React.ReactNode => {
      return columnDetail.render
        ? columnDetail.render(row, isActive, onRowClickMobile, onChange)
        : t(row[columnDetail.key]);
    },
    [row, isActive, onRowClickMobile, onChange, t]
  );

  const onButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setShowMore(!showMore);
    },
    [setShowMore, showMore]
  );

  const onRowClick = useCallback(() => {
    if (onClick && activeRow === undefined && isDesktop) {
      onClick(row);
    }
  }, [onClick, row, activeRow, isDesktop]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onCheck) {
      onCheck(e);
    }
    e.stopPropagation();
  };

  return (
    <>
      <tr className={`styled-table-row ${isRowClickable && rowClass} `} onClick={onRowClick} tabIndex={0}>
        {onCheck && (
          <td onClick={(e) => e.stopPropagation()}>
            <Checkbox className="checkbox" checked={checked} onChange={(e) => handleChange(e)} />
          </td>
        )}
        {columns.map((column, index) => (
          <td
            className={`${column.class} ${index === columns.length - 1 ? 'last-column-full-screen' : ''}`}
            key={`${row[dataKey]} ${column.key}`}
          >
            {renderData(column)}
          </td>
        ))}
        <td className="tablet-mobile-only data">
          <BdsIconButton
            size="sm"
            action={onButtonClick}
            icon={showMore ? CHEVRON_UP_V3 : CHEVRON_DOWN_V3}
            variant="transparent"
          />
        </td>
      </tr>
      <tr className="tablet-mobile-only row">
        <td colSpan={6}>
          <div className={`collapsable-section ${showMore ? 'active-row' : 'hide-row'}`}>
            {columns.map((column) => (
              <div className={`section-container ${column.class}`} key={`${row[dataKey]} ${column.key} mobile`}>
                <div className={`item-title ${column.key}`}>{t(column.name)}</div>
                <div className={`item-value ${column.key}`}>{renderData(column)}</div>
              </div>
            ))}
          </div>
        </td>
      </tr>
    </>
  );
};

export default React.memo(TableRow);
